/**
 * @module survey_type.transformer
 */
import { defaultLang, supportedLanguages } from '~/config/settings/app.settings';
import { text } from '~/tools/text';
import { categoryFrom } from './category.transformer';

/**
 * Transform SurveyType "names" array to translations dictionary.
 *
 * @param {Object} obj - destructured param object
 * @param {Array} obj.names - a set of "name/lang" objects from the server
 * @returns {Object} a dictionary of translated strings
 *
 * @see `/src/tools/propTypes` for example translations shape.
 *
 * @example names (from server)
 * names: [
 *   {
 *     "languageId": 1,
 *     "languageCode": "en-ca",
 *     "name": "Engagement"
 *   }
 * ]
 */
const surveyTypeTranslationsFrom = ({ names = [] }) => {
  const translations = supportedLanguages.reduce((dict, lang) => {
    // @ts-expect-error TS(2339): Property 'languageCode' does not exist on type 'ne... Remove this comment to see the full error message
    const nameObj = names.find((n) => n.languageCode === lang);

    // A collection of all translated strings for the given `lang`.
    const translation = {
      // @ts-expect-error TS(2339): Property 'name' does not exist on type 'never'.
      name: nameObj?.name,
    };

    return {
      ...dict,
      [lang]: translation,
    };
  }, {});

  return translations;
};

/**
 * Transform translation objects into a SurveyType "names" array.
 *
 * @param {Object} translations - a dictionary of translated strings
 * @param {Object} languageCodeIds - a dictionary that maps ISO codes to database IDs
 * @returns {Array} an array of "name/lang" objects formatted for the server
 *
 * @see ./language.transformer.js
 */
const surveyTypeTranslationsToNames = (translations, languageCodeIds) => {
  const names = Object.keys(translations).map((lang) => ({
    languageId: languageCodeIds[lang],
    languageCode: lang,
    name: translations[lang].name,
  }));

  return names;
};

/**
 * Transform a surveyType from the server form to the front-end form.
 *
 * @example surveyType (from server)
 * {
 *   "id": 3,
 *   "threshold": 5,
 *   "companyId": 123,
 *   "baseSurveyTypeId": 123,
 *   "name": "Engagement",
 *   "names": [
 *     {
 *       "languageId": 1,
 *       "languageCode": "en-ca",
 *       "name": "Engagement"
 *     }
 *   ],
 *   "categories": [
 *     {
 *       "id": 1,
 *       "name": "Leadership"
 *       "names": [
 *         {
 *           "languageId": 1,
 *           "languageCode": "en-ca",
 *           "name": "Leadership"
 *         }
 *       ],
 *       "isKeyFactor": null,
 *       "isBaseCategory": true
 *     }
 *   ]
 * }
 */
const surveyTypeFrom = (surveyType) => {
  // If the server did not provide any values for `surveyType.categories` set it
  // as `null` to indicate that there is simply "no data" not that the new value
  // should be an empty array.
  const transformedCategories = surveyType?.categories
    ? surveyType.categories.map((category) => categoryFrom(category, surveyType.id))
    : null;

  const translations = surveyTypeTranslationsFrom({
    names: surveyType.names,
  });

  const defaultName =
    surveyType.name || translations[defaultLang]?.name || text.findFirstTranslationFor(translations, 'name');

  const transformedSurveyType = {
    id: surveyType.id,
    companyId: surveyType.companyId,
    baseSurveyTypeId: surveyType.baseSurveyTypeId,
    name: defaultName,
    threshold: surveyType.threshold,
    categories: transformedCategories,
    translations,
  };

  // TODO: validate object shape

  return transformedSurveyType;
};

/**
 * Map list of survey types + their categories returned by the server.
 */
const surveyTypesFrom = (list = []) => {
  const surveyTypeList = list.map(surveyTypeFrom);

  return surveyTypeList;
};

export { surveyTypeFrom, surveyTypesFrom, surveyTypeTranslationsFrom, surveyTypeTranslationsToNames };
